/**
 * Latch hook patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stitch_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const latchHookPatternsPack: ToilePack = {
    slug: 'latch-hook-patterns',
    category: 'craft',
    name: 'Latch hook patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'rug', label: 'Round rug', subject: 'a latch hook chart for a round rug, concentric rings of five colours on a square canvas grid, the corners outside the circle left blank'},
        {key: 'mushroom', label: 'Mushroom', subject: 'a latch hook chart of a red toadstool with white spots on a pale green ground, bold simple shapes, each square one knot'},
        {key: 'rainbow', label: 'Rainbow', subject: 'a latch hook chart of a rainbow arch with two small clouds, six colour bands each three squares thick, on a cream ground'},
    ],
    styles: [
        {key: 'colour', label: 'Colour chart', style: 'a clean colour chart, every square filled flat in its colour with fine light-gray grid lines between them', palette: 'a limited palette of the yarn colours named', framing: 'the chart centred on a plain white sheet with an even margin'},
        {key: 'symbol', label: 'Symbol chart', style: 'a chart for black-and-white printing, every square carrying one small black symbol over a pale colour so each shade stays distinct', palette: 'pale colours kept under crisp black symbols', framing: 'the chart filling most of a white sheet with a narrow clean margin'},
        {key: 'graph', label: 'Graph paper', style: 'a hand-coloured worksheet, each square filled in with felt-tip pens, slightly uneven strokes and a few pencil guide marks, on blue squared graph paper', palette: 'bright felt-tip colours on pale blue grid paper', framing: 'the chart drawn on a plain sheet with a small margin all round'},
    ],
    directions: ['a unicorn', 'a dolphin', 'a sun'],
    directionHint: 'try: a unicorn',
    seo: {
        title: 'Latch hook patterns: round rug, mushroom, rainbow',
        metaDescription: 'Nine latch hook charts: a round rug, a mushroom and a rainbow, on a square canvas grid with one square per knot.',
        h1: 'Latch hook patterns: a round rug, a mushroom and a rainbow',
        tagline: 'Bold coloured charts on a canvas grid, one square per knot.',
        intro: 'Latch hook knots short lengths of yarn into rug canvas, for rugs, cushions and wall hangings with a soft pile. A chart puts one square on the canvas for each knot. These nine cover a round rug, a red toadstool and a rainbow arch. Each comes as a colour chart, a symbol chart, or a hand-coloured sheet. Type a different shape and the same canvas grid carries it.',
        useCases: [
            'a round rug',
            'a cushion front',
            "a wall hanging for a child's room",
            'a small doormat',
        ],
        faq: [
            {q: 'Why are the shapes so bold?', a: 'Bold shapes suit a pile, where fine lines blur.'},
            {q: 'Are the corners blank on the round rug?', a: 'Yes. The squares outside the circle are left blank.'},
            {q: 'Can I change the colours?', a: 'Yes. Type the yarn colours you want. The samples here keep theirs.'},
        ],
    },
}
